import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { endpoints } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import Logo from "@/components/Logo";

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await endpoints.login({ username, password });
      login(res.data.user);
      toast.success("Welcome back!");
      navigate("/");
    } catch (err) {
      const msg = err?.response?.status === 401
        ? "Invalid username or password."
        : "Unable to sign in. Please try again.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative grain overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1762811054950-b74e0a055c80?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBtb2Rlcm4lMjBsYW5kc2NhcGUlMjBkZXNpZ24lMjBiYWNreWFyZHxlbnwwfHx8fDE3ODE2NTIzNzJ8MA&ixlib=rb-4.1.0&q=85"
          className="absolute inset-0 w-full h-full object-cover"
          alt="Modern landscaping"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0c1929]/85 via-[#0c1929]/50 to-transparent" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <Logo size={56} />
            <div>
              <div className="font-display font-bold text-lg leading-tight">
                STAR <span className="text-primary">CAPITAL</span>
              </div>
              <div className="text-xs uppercase tracking-widest text-white/70">Landscaping · Austin, TX</div>
            </div>
          </div>
          <div className="max-w-md">
            <div className="text-xs uppercase tracking-widest gold-text font-semibold">Operations Platform</div>
            <h2 className="font-display text-4xl xl:text-5xl font-bold mt-3 leading-tight">
              Cultivate every customer relationship.
            </h2>
            <p className="text-white/70 mt-4">
              Estimates, jobs, crews, invoices, and payments — built for landscaping companies that take pride in their craft.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12 bg-background">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 lg:hidden mb-8">
            <Logo size={44} />
            <div className="font-display font-bold text-lg">
              STAR <span className="text-primary">CAPITAL</span>
            </div>
          </div>

          <h1 className="font-display text-3xl font-bold tracking-tight">Sign in</h1>

          <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="login-form" autoComplete="off">
            <div className="space-y-1.5">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your username"
                data-testid="login-username"
                autoComplete="off"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  data-testid="login-password"
                  autoComplete="new-password"
                  required
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowPwd((s) => !s)}
                  data-testid="toggle-password"
                  aria-label="Toggle password visibility"
                >
                  {showPwd ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div
                className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2"
                data-testid="login-error"
                role="alert"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-11 font-semibold"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? "Signing in..." : "Sign in"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
